/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Check, X, ChevronLeft, ChevronRight, ChevronDown, Clock, Shield, CalendarOff, Filter } from 'lucide-react';
import { isGreekHoliday, getLeaveTypeInfo } from '../utils/vacationUtils';

const canManagerActOn = (request, requestUser, currentUser, managerLevel) => {
  if (currentUser?.role !== 'manager') return false;
  if (!requestUser) return false;
  const isDirectReport = (requestUser.manager_id || requestUser.managerId) === currentUser.id;

  if (managerLevel === 2) {
    if (request.status === 'pending' && isDirectReport) return true;
    if (request.status === 'manager1_approved') return true;
    return false;
  }
  return request.status === 'pending' && isDirectReport;
};

const STATUS_META = {
  pending:           { label: 'Εκκρεμεί',           bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  manager1_approved: { label: 'Εγκρίθηκε από Δ1',   bg: '#DBEAFE', text: '#1E40AF', dot: '#3B82F6' },
  approved:          { label: 'Εγκρίθηκε',          bg: '#DCFCE7', text: '#166534', dot: '#16A34A' },
  rejected:          { label: 'Απορρίφθηκε',        bg: '#FEE2E2', text: '#991B1B', dot: '#DC2626' },
  manager1_rejected: { label: 'Απορρίφθηκε από Δ1', bg: '#FEE2E2', text: '#991B1B', dot: '#DC2626' },
};

const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.pending;

const groupedByDepartment = (entries) => {
  const groups = new Map();

  entries.forEach(entry => {
    const key = entry.department?.id ?? 'none';
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: entry.department?.name || 'Χωρίς τμήμα',
        color: entry.department?.color || '#9CA3AF',
        entries: [],
      });
    }
    groups.get(key).entries.push(entry);
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'none') return 1;
    if (b.key === 'none') return -1;
    return a.name.localeCompare(b.name, 'el');
  });
};

const DayPanel = ({ day, entries, onClose, onDecision, decidingId }) => {
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const deptDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
        setDeptDropdownOpen(false);
      }
    };
    if (deptDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [deptDropdownOpen]);

  if (!day) return null;

  const dateObj = new Date(day.date);
  const dateLabel = dateObj.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' });

  const availableDepts = Array.from(
    new Map(
      entries.map(e => [
        e.department?.id ?? 'none',
        { id: e.department?.id ?? 'none', name: e.department?.name || 'Χωρίς τμήμα', color: e.department?.color || '#9CA3AF' }
      ])
    ).values()
  ).sort((a, b) => {
    if (a.id === 'none') return 1;
    if (b.id === 'none') return -1;
    return a.name.localeCompare(b.name, 'el');
  });

  const toggleDept = (id) => {
    setSelectedDepts(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const pendingCount = entries.filter(e => e.request.status === 'pending' || e.request.status === 'manager1_approved').length;

  const filteredEntries = entries
    .filter(e => selectedDepts.length === 0 || selectedDepts.includes(e.department?.id ?? 'none'))
    .filter(e => !pendingOnly || e.request.status === 'pending' || e.request.status === 'manager1_approved');

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-[380px] bg-white dark:bg-jewel-bg z-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-jewel-border flex items-center justify-between flex-shrink-0">
          <div>
            <p className="font-semibold text-gray-900 dark:text-jewel-text capitalize">{dateLabel}</p>
            <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-0.5">
              {entries.length === 0
                ? 'Κανένας σε άδεια'
                : `${entries.length} ${entries.length === 1 ? 'άτομο' : 'άτομα'} σε άδεια`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-jewel-surface rounded-lg transition-colors flex-shrink-0">
            <X className="h-5 w-5 text-gray-500 dark:text-jewel-text-muted" />
          </button>
        </div>

        {day.holiday && (
          <div className="px-5 py-2.5 bg-red-50 dark:bg-red-900/30 border-b border-red-100 dark:border-red-800 flex items-center gap-2">
            <span className="text-red-500">🎉</span>
            <span className="text-xs font-semibold text-red-700 dark:text-red-400">{day.holiday.holiday_name}</span>
          </div>
        )}

        {(availableDepts.length > 1 || pendingCount > 0) && (
          <div className="flex items-center gap-2 px-5 pt-2.5 pb-2.5 border-b border-gray-200 dark:border-jewel-border flex-shrink-0">
            {availableDepts.length > 1 && (
              <div ref={deptDropdownRef} className="relative flex-1 min-w-0">
                <button
                  onClick={() => setDeptDropdownOpen(prev => !prev)}
                  className="flex items-center justify-between w-full px-2.5 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-jewel-text-muted border border-gray-200 dark:border-jewel-border rounded-lg hover:border-gray-300 dark:hover:border-jewel-text-muted transition-colors"
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <Filter className="h-3 w-3 flex-shrink-0" />
                    {selectedDepts.length === 0
                      ? 'Όλα τα τμήματα'
                      : selectedDepts.length === 1
                      ? availableDepts.find(d => d.id === selectedDepts[0])?.name
                      : `${selectedDepts.length} τμήματα`}
                  </span>
                  <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform ${deptDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {deptDropdownOpen && (
                  <div className="absolute left-0 right-0 z-10 mt-1 bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedDepts([]); setDeptDropdownOpen(false); }}
                      className="w-full flex items-center px-3 py-1.5 text-[11px] font-semibold text-blue-600 dark:text-jewel-teal-tint hover:bg-gray-50 dark:hover:bg-jewel-surface transition-colors"
                    >
                      Όλα τα τμήματα
                    </button>
                    <div className="h-px bg-gray-100 dark:bg-jewel-border" />
                    {availableDepts.map(dept => {
                      const isActive = selectedDepts.includes(dept.id);
                      return (
                        <label
                          key={dept.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-gray-700 dark:text-jewel-text hover:bg-gray-50 dark:hover:bg-jewel-surface cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => toggleDept(dept.id)}
                            className="w-3 h-3 text-blue-600 rounded border-gray-300 dark:border-jewel-border focus:ring-blue-500"
                          />
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />
                          <span className="truncate">{dept.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {pendingCount > 0 && (
              <button
                onClick={() => setPendingOnly(prev => !prev)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border transition-colors flex-shrink-0 ${
                  pendingOnly
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    : 'border-gray-200 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted hover:border-gray-300 dark:hover:border-jewel-text-muted'
                }`}
              >
                <Clock className="h-3 w-3" />
                Εκκρεμή
                <span className={`text-[10px] font-bold ${pendingOnly ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-jewel-text-muted'}`}>
                  ({pendingCount})
                </span>
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {filteredEntries.length === 0 ? (
            <div className="text-center py-10">
              <CalendarOff className="h-10 w-10 mx-auto mb-2 text-gray-300 dark:text-jewel-text-muted" />
              <p className="text-sm text-gray-400 dark:text-jewel-text-muted">
                {entries.length === 0 ? 'Δεν υπάρχουν άδειες αυτή την ημέρα' : 'Κανένα αποτέλεσμα για τα επιλεγμένα φίλτρα'}
              </p>
            </div>
          ) : (
            groupedByDepartment(filteredEntries).map(group => (
              <div key={group.key}>
                <div className="flex items-center gap-2 mb-2 px-0.5">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  <span className="text-[11px] font-bold text-gray-500 dark:text-jewel-text-muted uppercase tracking-wide truncate">
                    {group.name}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-jewel-text-muted flex-shrink-0">
                    ({group.entries.length})
                  </span>
                </div>
                <div className="space-y-3">
                  {group.entries.map(entry => {
              const meta = getStatusMeta(entry.request.status);
              const isGranted = entry.request.manager_granted || entry.request.managerGranted;
              const isUnpaid = (entry.request.leave_type || entry.request.leaveType) === 'unpaid';
              const leaveInfo = getLeaveTypeInfo(entry.request.leave_type || entry.request.leaveType);
              const isDeciding = decidingId === entry.request.id;

              return (
                <div key={entry.request.id} className="border border-gray-200 dark:border-jewel-border rounded-xl p-3.5">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                      style={{ backgroundColor: entry.department?.color || '#3B82F6' }}
                    >
                      {(entry.user?.name || '?').split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-jewel-text truncate">{entry.user?.name || 'Άγνωστος'}</p>
                      <p className="text-[11px] text-gray-500 dark:text-jewel-text-muted truncate">{entry.department?.name || 'Χωρίς τμήμα'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {isGranted ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold"
                        style={{
                          backgroundColor: isUnpaid ? '#FFF7ED' : '#F5F3FF',
                          color: isUnpaid ? '#C2410C' : '#6D28D9',
                        }}
                      >
                        {isUnpaid ? 'Χορηγήθηκε - Άνευ' : 'Χορηγήθηκε'}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold"
                        style={{ backgroundColor: meta.bg, color: meta.text }}
                      >
                        {meta.label}
                      </span>
                    )}
                    <span className="text-[11px] text-gray-500 dark:text-jewel-text-muted flex items-center gap-1">
                      <span>{leaveInfo.icon}</span>{leaveInfo.label}
                    </span>
                  </div>

                  <p className="text-[11px] text-gray-500 dark:text-jewel-text-muted mb-1">
                    {new Date(entry.request.start_date || entry.request.startDate).toLocaleDateString('el-GR')}
                    {' '}–{' '}
                    {new Date(entry.request.end_date || entry.request.endDate).toLocaleDateString('el-GR')}
                  </p>

                  {entry.request.reason && (
                    <p className="text-xs text-gray-600 dark:text-jewel-text italic bg-gray-50 dark:bg-jewel-bg/50 rounded-lg px-2.5 py-1.5 mt-2 border border-gray-100 dark:border-jewel-border">
                      "{entry.request.reason}"
                    </p>
                  )}

                  {entry.canAct && (
                    <div className="flex gap-2 mt-3">
                      <button
                        disabled={isDeciding}
                        onClick={() => onDecision(entry.request.id, 'approved', entry.actAsLevel)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
                      >
                        <Check className="h-3.5 w-3.5" /> Έγκριση
                      </button>
                      <button
                        disabled={isDeciding}
                        onClick={() => onDecision(entry.request.id, 'rejected', entry.actAsLevel)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
                      >
                        <X className="h-3.5 w-3.5" /> Απόρριψη
                      </button>
                    </div>
                  )}
                </div>
              );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

const CalendarDetailedView = ({
  filteredVacationRequests,
  userDatabase,
  departments,
  holidays,
  currentUser,
  currentMonth,
  currentYear,
  goToPreviousMonth,
  goToNextMonth,
  monthName,
  onRequestDecision,
}) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [decidingId, setDecidingId] = useState(null);

  const managerLevel = currentUser.managerLevel || currentUser.manager_level || 1;

  const getDepartmentByUserId = (userId) => {
    const user = userDatabase.find(u => u.id === userId);
    if (!user) return null;
    const departmentId = user.department_id || user.departmentId;
    return departments.find(dept => dept.id === departmentId);
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const vacationsOnThisDay = filteredVacationRequests.filter(req => {
        const startDate = new Date(req.start_date || req.startDate);
        const endDate = new Date(req.end_date || req.endDate);
        const currentDate = new Date(dateStr);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        return currentDate >= startDate && currentDate <= endDate;
      });

      const holiday = isGreekHoliday(dateStr, holidays);

      days.push({ day, date: dateStr, vacations: vacationsOnThisDay, holiday });
    }

    return days;
  }, [filteredVacationRequests, currentMonth, currentYear, holidays]);

  const buildEntriesForDay = (day) => {
    if (!day) return [];
    return day.vacations.map(request => {
      const userId = request.user_id || request.userId;
      const user = userDatabase.find(u => u.id === userId);
      const department = getDepartmentByUserId(userId);
      const isGranted = request.manager_granted || request.managerGranted;
      const canAct = !isGranted && canManagerActOn(request, user, currentUser, managerLevel);
      const actAsLevel = managerLevel === 2 && request.status === 'pending' ? 1 : managerLevel;

      return { request, user, department, canAct, actAsLevel };
    }).sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || '', 'el'));
  };

  const selectedDayLive = selectedDay
    ? calendarDays.find(d => d && d.date === selectedDay.date) || null
    : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const panelEntries = useMemo(() => buildEntriesForDay(selectedDayLive), [selectedDayLive, userDatabase, departments, currentUser, managerLevel]);

  const handleDayClick = (day) => {
    if (!day || day.vacations.length === 0) return;
    setSelectedDay(day);
  };

  const handleDecision = async (requestId, decision, actAsLevel) => {
    setDecidingId(requestId);
    try {
      await onRequestDecision(requestId, decision, actAsLevel);
    } finally {
      setDecidingId(null);
    }
  };

  const dayNames = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'];

  const DOT_COLORS = {
    approved: '#16A34A',
    manager1_approved: '#3B82F6',
    pending: '#F59E0B',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={goToPreviousMonth} className="p-2 rounded-lg border border-gray-200 dark:border-jewel-border hover:bg-gray-50 dark:hover:bg-jewel-surface transition-colors">
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-jewel-text" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-jewel-text">{monthName}</h2>
          <button onClick={goToNextMonth} className="p-2 rounded-lg border border-gray-200 dark:border-jewel-border hover:bg-gray-50 dark:hover:bg-jewel-surface transition-colors">
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-jewel-text" />
          </button>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-jewel-text-muted">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: DOT_COLORS.approved }} />Εγκρίθηκε</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: DOT_COLORS.manager1_approved }} />Δ1 Έγκριση</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: DOT_COLORS.pending }} />Εκκρεμεί</span>
        </div>
      </div>

      <div className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-jewel-bg">
          {dayNames.map(d => (
            <div key={d} className="p-2.5 text-center text-xs font-medium text-gray-500 dark:text-jewel-text-muted border-r border-gray-200 dark:border-jewel-border last:border-r-0">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const statusesPresent = day
              ? [...new Set(day.vacations.map(v => v.status))].filter(s => DOT_COLORS[s])
              : [];
            return (
              <div
                key={index}
                onClick={() => handleDayClick(day)}
                className={`min-h-[72px] p-2 border-r border-b border-gray-200 dark:border-jewel-border last:border-r-0 transition-colors ${
                  day?.holiday ? 'bg-red-50 dark:bg-red-900/20' : ''
                } ${day && day.vacations.length > 0 ? 'cursor-pointer hover:bg-blue-50/50 dark:hover:bg-jewel-teal/15' : ''}`}
              >
                {day && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${day.holiday ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-jewel-text'}`}>
                        {day.day}
                      </span>
                      {day.holiday && <span className="text-red-500 dark:text-red-400 text-[10px]">🎉</span>}
                    </div>
                    {day.vacations.length > 0 && (
                      <div className="mt-2.5">
                        <div className="flex gap-1.5">
                          {statusesPresent.slice(0, 3).map(status => (
                            <span key={status} className="w-2.5 h-2.5 rounded-full" style={{ background: DOT_COLORS[status] }} />
                          ))}
                        </div>
                        <div className="text-xs font-medium text-gray-500 dark:text-jewel-text-muted mt-1.5">
                          {day.vacations.length} {day.vacations.length === 1 ? 'άτομο' : 'άτομα'}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDayLive && (
        <DayPanel
          key={selectedDayLive.date}
          day={selectedDayLive}
          entries={panelEntries}
          onClose={() => setSelectedDay(null)}
          onDecision={handleDecision}
          decidingId={decidingId}
        />
      )}
    </div>
  );
};

export default CalendarDetailedView;